import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { MatRadioModule } from "@angular/material/radio";
import { Subscription } from "rxjs";

import {
  AlcanceReporteQa,
  CalidadCodigoRes,
  ContenidoReporteQa,
  ProyectoResumen,
  ReporteEmitido,
} from "src/app/shared/model/calidad-codigo/calidad-codigo.model";
import { CalidadCodigoService } from "../calidad-codigo.service";
import { ESTADO_PROYECTO, EstadoCc, estadoDe } from "../cc-estados";
import { plural } from "../cc-formato";
import { EMITIENDO_PDF } from "../reporte/actualizacion-cc";

/** Lo que recibe el cuadro: los proyectos a la vista y cómo se filtraron. */
export interface DatosDialogoReporteQa {
  /** Los proyectos del tablero con el filtro vigente; sin ellos, el cuadro consulta el tablero completo. */
  proyectos?: ProyectoResumen[];
  /** "con el filtro «No pasa»": cómo se llegó a esa lista (vacío = el tablero completo). */
  filtroTexto?: string;
  /** El filtro, la búsqueda y el gate con que se armó esa lista; sin ellos, el tablero completo (TODOS). */
  alcance?: AlcanceReporteQa;
}

/** Qué proyectos entran: los de la lista, solo los que no pasan o los palomeados. */
type AlcanceQa = "LISTA" | "NO_PASAN" | "ELEGIDOS";

interface RenglonProyecto {
  id: number;
  nombre: string;
  estado: EstadoCc<ProyectoResumen["estado"]>;
}

const ID_TITULO = "cc-reporte-qa-titulo";

/**
 * Abre el cuadro «Nuevo reporte para QA» con la forma de los diálogos
 * estándar (app-standard-dialog y la caja .app-dialog de styles.css, que
 * tema-overlays.css pinta en oscuro y cristal), como dialogo-pin-bsc.
 *
 * @returns el cuadro: afterClosed() da el reporte emitido (con sus datos,
 *          para armar el PDF) o undefined si se canceló.
 */
export function abrirDialogoReporteQa(
  dialog: MatDialog,
  datos: DatosDialogoReporteQa,
): MatDialogRef<DialogoReporteQaComponent, ReporteEmitido> {
  // Debajo de la barra superior (fija, 68 px): un cuadro alto quedaría tapado por ella si se centrara.
  return dialog.open<DialogoReporteQaComponent, DatosDialogoReporteQa, ReporteEmitido>(DialogoReporteQaComponent, {
    data: datos,
    width: "560px",
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 100px)",
    position: { top: "84px" },
    panelClass: "app-standard-dialog",
    backdropClass: "app-standard-dialog-backdrop",
    ariaLabelledBy: ID_TITULO,
    autoFocus: "first-tabbable",
    disableClose: true,
  });
}

/**
 * Calidad de código · el cuadro del reporte para QA: qué proyectos entran
 * (los de la lista, solo los que no pasan o elegidos uno por uno) y qué
 * contenido lleva (criterios que fallan y desde cuándo, aceptados,
 * trazabilidad). «Emitir» le pide al servidor traer lo último de SonarQube y
 * congelar la foto (folio y huella) y devuelve el reporte; quien lo abre
 * descarga el PDF. Queda en la bandeja.
 */
@Component({
  selector: "app-dialogo-reporte-qa",
  standalone: true,
  templateUrl: "./dialogo-reporte-qa.component.html",
  styleUrls: ["../calidad-codigo.shared.css", "./dialogo-reporte-qa.component.css"],
  imports: [CommonModule, FormsModule, MatDialogContent, MatDialogActions, MatRadioModule, MatCheckboxModule],
})
export class DialogoReporteQaComponent implements OnInit, OnDestroy {
  readonly idTitulo = ID_TITULO;
  /** El globo de «Emitir y descargar PDF» mientras emite: el servidor consulta antes SonarQube. */
  readonly emitiendoPdf = EMITIENDO_PDF;

  proyectos: RenglonProyecto[] = [];
  alcance: AlcanceQa = "LISTA";
  elegidos = new Set<number>();
  contenido: ContenidoReporteQa = { criterios: true, aceptados: true, trazabilidad: true };
  cargando = false;
  enviando = false;
  /** El error del servidor o lo que falta para emitir. */
  mensaje = "";

  private readonly subs: Subscription[] = [];
  private cerrado = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly datos: DatosDialogoReporteQa,
    private readonly dialogRef: MatDialogRef<DialogoReporteQaComponent, ReporteEmitido>,
    private readonly servicio: CalidadCodigoService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.dialogRef.backdropClick().subscribe(() => this.cancelar()),
      this.dialogRef.keydownEvents().subscribe((ev) => {
        if (ev.key === "Escape") {
          ev.preventDefault();
          this.cancelar();
        }
      }),
    );
    if (this.datos.proyectos) {
      this.proyectos = this.datos.proyectos.map((p) => this.renglon(p));
      return;
    }
    this.cargando = true;
    this.subs.push(
      this.servicio.tablero("TODOS", "", null).subscribe({
        next: (res) => this.aplicaLista(res),
        error: () => {
          this.cargando = false;
          this.mensaje = "No se pudo leer la lista de proyectos: sin conexión con el servidor.";
          this.pinta();
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.cerrado = true;
    this.subs.forEach((s) => s.unsubscribe());
  }

  private aplicaLista(res: CalidadCodigoRes): void {
    this.cargando = false;
    if (res?.codigoError) {
      this.mensaje = this.textoDeError(res);
    } else {
      this.proyectos = (res.proyectos ?? []).map((p) => this.renglon(p));
    }
    this.pinta();
  }

  private renglon(p: ProyectoResumen): RenglonProyecto {
    return { id: p.id, nombre: p.nombre, estado: estadoDe(ESTADO_PROYECTO, p.estado) };
  }

  /** "Los 12 del tablero" / "Los 6 de la lista (con el filtro «No pasa»)". */
  get textoLista(): string {
    const n = plural(this.proyectos.length, "proyecto", "proyectos");
    return this.datos.filtroTexto ? `Los ${n} de la lista (${this.datos.filtroTexto})` : `Los ${n} del tablero`;
  }

  get noPasan(): number {
    return this.proyectos.filter((p) => p.estado.valor === "NO_PASA").length;
  }

  /** Los ids que viajan con el alcance elegido. */
  get ids(): number[] {
    switch (this.alcance) {
      case "NO_PASAN":
        return this.proyectos.filter((p) => p.estado.valor === "NO_PASA").map((p) => p.id);
      case "ELEGIDOS":
        return this.proyectos.filter((p) => this.elegidos.has(p.id)).map((p) => p.id);
      default:
        return this.proyectos.map((p) => p.id);
    }
  }

  get listo(): boolean {
    return !this.enviando && !this.cargando && this.ids.length > 0;
  }

  alterna(id: number, elegido: boolean): void {
    if (elegido) {
      this.elegidos.add(id);
    } else {
      this.elegidos.delete(id);
    }
    this.mensaje = "";
  }

  elegido(id: number): boolean {
    return this.elegidos.has(id);
  }

  emitir(): void {
    if (this.enviando) {
      return;
    }
    if (this.ids.length === 0) {
      this.mensaje = this.alcance === "ELEGIDOS" ? "Elige al menos un proyecto." : "No hay proyectos que reportar con ese alcance.";
      return;
    }
    this.enviando = true;
    this.mensaje = "";
    this.pinta();
    const { alcance, ids } = this.alcanceYIds();
    this.subs.push(
      this.servicio.reporteQa(alcance, ids, { ...this.contenido }).subscribe({
        next: (res) => {
          this.enviando = false;
          if (!res || res.codigoError || !res.reporte) {
            this.mensaje = this.textoDeError(res);
            this.pinta();
            return;
          }
          this.cerrado = true;
          this.dialogRef.close(res.reporte);
        },
        error: () => {
          this.enviando = false;
          this.mensaje = "Sin conexión con el servidor. No se emitió nada.";
          this.pinta();
        },
      }),
    );
  }

  cancelar(): void {
    if (this.enviando || this.cerrado) {
      return;
    }
    this.cerrado = true;
    this.dialogRef.close();
  }

  /**
   * Cómo viaja el alcance al servidor. Por filtro (sin ids: el servidor rehace
   * la misma lista y la foto dice «los 12 del tablero» o «6 con el filtro «No
   * pasa»») para «los de la lista», y para «solo los que no pasan» cuando la
   * lista venía de Todos o de No pasa; por ids (la foto dice «elegidos a mano»)
   * para los proyectos palomeados y para «solo los que no pasan» de una lista
   * con otro filtro, que el servidor no podría rehacer con un solo filtro.
   */
  private alcanceYIds(): { alcance: AlcanceReporteQa; ids: number[] | null } {
    const lista: AlcanceReporteQa = this.datos.alcance ?? { filtro: "TODOS" };
    if (this.alcance === "LISTA") {
      return { alcance: lista, ids: null };
    }
    if (this.alcance === "NO_PASAN" && (lista.filtro === "TODOS" || lista.filtro === "NO_PASA")) {
      return { alcance: { ...lista, filtro: "NO_PASA" }, ids: null };
    }
    return { alcance: lista, ids: this.ids };
  }

  trackProyecto(_: number, p: RenglonProyecto): number {
    return p.id;
  }

  private textoDeError(res: CalidadCodigoRes | null | undefined): string {
    if (res?.codigoError === "AUTH-001") {
      return "Tu sesión caducó. Vuelve a entrar.";
    }
    return res?.mensajeError || "No se pudo emitir el reporte. No se guardó nada.";
  }

  /** La app corre sin zonas: lo que llega de una petición se pinta a mano. */
  private pinta(): void {
    if (!this.cerrado) {
      this.cdr.detectChanges();
    }
  }
}
